import {
  fetchGroupProductsWithVariants,
  firstAvailableVariant,
  amountToCents,
  labelFromMetafield,
  uniqueByHandle,
  sortByCurrentHandle,
  GroupProductWithVariants,
  hasKey,
  isRecord,
} from "./product-groups-core";
import { formatMoney } from "../utils/money";
import { translate } from "../utils/i18n";

function readApplicableDiscount(): number {
  const discountNode = document.querySelector(
    "[data-product-data] [data-applicable-discount]"
  );
  if (!(discountNode instanceof HTMLElement)) return 0;
  const raw =
    discountNode.getAttribute("data-applicable-discount") ||
    discountNode.dataset.applicableDiscount ||
    "0";
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function refreshBundleDiscountUI(root: ParentNode = document): void {
  const discountPercent = readApplicableDiscount();
  const discountLabel = translate("product.discount.offer", "Fresh Offer");

  const discountBadges = root.querySelectorAll<HTMLElement>(
    ".bundle-items [data-discount-badge]"
  );
  discountBadges.forEach((badge) => {
    const badgeText = badge.querySelector<HTMLElement>("[data-discount-text]");
    if (discountPercent > 0 && badgeText) {
      badgeText.textContent = `${discountLabel} ${discountPercent} %`;
      badge.removeAttribute("hidden");
    } else {
      badge.setAttribute("hidden", "");
    }
  });

  const itemCards = root.querySelectorAll<HTMLElement>(
    ".bundle-items [data-bundle-item], .bundle-items .bundle-item"
  );
  itemCards.forEach((item) => {
    const baseCentsRaw = item.getAttribute("data-base-price-cents") || "";
    const baseCents = parseInt(baseCentsRaw, 10);
    const comparePriceEl = item.querySelector<HTMLElement>(
      "[data-compare-price]"
    );
    const salePriceEl = item.querySelector<HTMLElement>("[data-sale-price]");
    if (!salePriceEl || !Number.isFinite(baseCents)) return;

    const formatMoneyMajor = (val: number) => formatMoney(val / 100);
    if (discountPercent > 0) {
      const itemDiscount = Math.floor(baseCents * (discountPercent / 100));
      const discounted = Math.max(baseCents - itemDiscount, 0);
      if (comparePriceEl) {
        comparePriceEl.textContent = formatMoneyMajor(baseCents);
        comparePriceEl.removeAttribute("hidden");
      }
      salePriceEl.textContent = formatMoneyMajor(discounted);
      salePriceEl.classList.add("text-on-sale");
      salePriceEl.classList.remove("text-subdued");
    } else {
      if (comparePriceEl) comparePriceEl.setAttribute("hidden", "");
      salePriceEl.textContent = formatMoneyMajor(baseCents);
      salePriceEl.classList.remove("text-on-sale");
      salePriceEl.classList.add("text-subdued");
    }
  });
}

function createBundleGroupElement(
  products: GroupProductWithVariants[],
  currentHandle: string,
  inputName: string,
  quantity: number
): HTMLElement {
  const groupEl = document.createElement("div");
  groupEl.className = "bundle-items";

  const uniqueProducts = uniqueByHandle(products);
  const sorted = sortByCurrentHandle(uniqueProducts, currentHandle);
  const firstAvailable = sorted.find(
    (product) => firstAvailableVariant(product)?.availableForSale
  );
  const activeVariantId =
    firstAvailableVariant(firstAvailable || sorted[0])?.id || null;

  const numericId = (globalId: string | null): string | null => {
    if (!globalId) return null;
    const match = globalId.match(/(\d+)$/);
    return match ? match[1] : null;
  };

  const compareAtAmount = (value: unknown): string | null => {
    if (!isRecord(value)) return null;
    const compareAt = Reflect.get(value, "compareAtPrice");
    if (!isRecord(compareAt)) return null;
    const amount = Reflect.get(compareAt, "amount");
    return typeof amount === "string" ? amount : null;
  };

  const discountPercent = readApplicableDiscount();
  const discountLabel = translate("product.discount.offer", "Fresh Offer");

  const isSafeHttpUrl = (url: string): boolean => /^https?:\/\//.test(url);
  const cloneFromTemplate = (key: string): Element | null => {
    const templateEl =
      document.querySelector<HTMLTemplateElement>(
        `template[data-template="${key}"]`
      ) || (document.getElementById(key) as HTMLTemplateElement | null);
    if (!(templateEl instanceof HTMLTemplateElement)) return null;
    const firstTemplateChild = templateEl.content.firstElementChild;
    if (!firstTemplateChild) return null;
    const clonedElement = firstTemplateChild.cloneNode(true);
    return clonedElement instanceof Element ? clonedElement : null;
  };
  const bySel = (root: ParentNode, sel: string): Element | null =>
    root.querySelector(sel);

  let index = 0;
  for (const product of sorted) {
    const variant = firstAvailableVariant(product);
    if (!variant) continue;
    const isActive = activeVariantId ? variant.id === activeVariantId : false;
    const imageUrl = (variant.image?.url || product.featuredImage?.url) ?? "";
    const unitPriceCents = amountToCents(
      variant.price?.amount || variant.priceV2?.amount || null
    );
    const qtyEach = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    const priceCents = unitPriceCents * qtyEach;
    const unitCompareCents = amountToCents(compareAtAmount(variant));
    const compareCents = unitCompareCents * qtyEach;
    const hasSale = compareCents > priceCents && compareCents > 0;
    const savingsCents = hasSale ? compareCents - priceCents : 0;
    const savingsPercent =
      hasSale && compareCents > 0
        ? Math.round((savingsCents / compareCents) * 100)
        : 0;
    const settings = window.themeVariables?.settings;
    const showDiscount = settings?.showDiscount !== false;
    const usePercent = settings?.discountMode === "percentage";
    const savingsLabel =
      usePercent && savingsPercent > 0
        ? `${savingsPercent}%`
        : formatMoney(savingsCents / 100);
    const showSaleBadge = showDiscount && hasSale && index > 0;

    const itemBase = cloneFromTemplate("tpl-bundle-item");
    if (itemBase && itemBase instanceof HTMLElement) {
      const itemEl = itemBase;
      if (isActive) itemEl.classList.add("is-active");
      itemEl.setAttribute("data-variant-id", numericId(variant.id) ?? "");
      itemEl.setAttribute("data-base-price-cents", String(priceCents));

      const imageEl = bySel(itemEl, "[data-bundle-img]");
      if (imageEl instanceof HTMLImageElement) {
        if (imageUrl && isSafeHttpUrl(imageUrl)) {
          imageEl.src = imageUrl;
          imageEl.alt = product.title;
          imageEl.removeAttribute("hidden");
        } else {
          imageEl.parentElement?.remove();
        }
      }

      const productLinkEl = bySel(itemEl, "[data-bundle-link]");
      const quantityEl = bySel(itemEl, "[data-bundle-quantity]");
      if (productLinkEl instanceof HTMLAnchorElement) {
        const shopifyRoot = window.Shopify?.routes?.root;
        const root =
          typeof shopifyRoot === "string" && shopifyRoot.length > 0
            ? shopifyRoot
            : "/";
        productLinkEl.href = `${root}products/${encodeURIComponent(product.handle)}`;
        const qty = Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
        productLinkEl.textContent = product.title;
        if (quantityEl instanceof HTMLElement) {
          if (qty > 0) {
            quantityEl.textContent = String(qty);
            quantityEl.removeAttribute("hidden");
          } else {
            quantityEl.setAttribute("hidden", "");
          }
        }
      }

      const variantTitleEl = bySel(itemEl, "[data-variant-title]");
      if (variantTitleEl instanceof HTMLElement) {
        if (variant.title && variant.title !== "Default Title") {
          variantTitleEl.textContent = variant.title;
          variantTitleEl.removeAttribute("hidden");
        }
      }

      const comparePriceEl = bySel(itemEl, "[data-compare-price]");
      const salePriceEl = bySel(itemEl, "[data-sale-price]");
      if (salePriceEl instanceof HTMLElement) {
        if (discountPercent > 0) {
          const itemDiscount = Math.floor(priceCents * (discountPercent / 100));
          const discounted = Math.max(priceCents - itemDiscount, 0);
          if (comparePriceEl instanceof HTMLElement) {
            comparePriceEl.textContent = formatMoney(priceCents / 100);
            comparePriceEl.removeAttribute("hidden");
          }
          salePriceEl.textContent = formatMoney(discounted / 100);
          salePriceEl.classList.add("text-on-sale");
          salePriceEl.classList.remove("text-subdued");
        } else {
          if (comparePriceEl instanceof HTMLElement && hasSale) {
            comparePriceEl.textContent = formatMoney(compareCents / 100);
            comparePriceEl.removeAttribute("hidden");
          }
          salePriceEl.textContent = formatMoney(priceCents / 100);
          salePriceEl.classList.toggle("text-on-sale", hasSale);
          salePriceEl.classList.toggle("text-subdued", !hasSale);
        }
      }

      const onSaleBadge = bySel(itemEl, "[data-sale-badge]");
      if (onSaleBadge instanceof HTMLElement) {
        onSaleBadge.setAttribute("hidden", "");
      }

      const discountBadge = bySel(itemEl, "[data-discount-badge]");
      const discountText = bySel(itemEl, "[data-discount-text]");
      if (
        discountPercent > 0 &&
        discountBadge instanceof HTMLElement &&
        discountText instanceof HTMLElement
      ) {
        discountText.textContent = `${discountLabel} ${discountPercent} %`;
        discountBadge.removeAttribute("hidden");
      }

      groupEl.appendChild(itemEl);
    }
    index += 1;
  }

  const inputsEl = document.createElement("div");
  inputsEl.className = "bundle-item__variant-inputs";
  let checkedAssigned = false;
  for (const product of sorted) {
    const variant = firstAvailableVariant(product);
    if (!variant) continue;
    const priceCents = amountToCents(
      variant.price?.amount || variant.priceV2?.amount || null
    );
    const vidNumeric = numericId(variant.id) ?? "";
    const inputId = `${inputName}_${vidNumeric}`;
    const optionLabel = labelFromMetafield(product);
    const disabled = variant.availableForSale ? "" : "disabled";
    const checked =
      (!checkedAssigned && variant.availableForSale) ||
      (activeVariantId ? variant.id === activeVariantId : false);
    if (checked) checkedAssigned = true;

    const wrapper = cloneFromTemplate("tpl-bundle-input");
    if (wrapper) {
      const input = bySel(wrapper, "[data-variant-radio-input]");
      const labelEl = bySel(wrapper, "[data-bundle-swatch]");
      if (
        input instanceof HTMLInputElement &&
        labelEl instanceof HTMLLabelElement
      ) {
        input.id = inputId;
        input.name = inputName;
        input.value = vidNumeric;
        input.dataset.variantPrice = String(priceCents);
        const qtyEach = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
        input.dataset.variantQty = String(qtyEach);
        if (checked) input.checked = true;
        if (disabled) input.disabled = true;
        labelEl.setAttribute("for", inputId);
        labelEl.textContent = optionLabel;
        labelEl.className = `block-swatch ${variant.availableForSale ? "available" : "unavailable"}`;
        // append only input + label to match existing CSS
        inputsEl.appendChild(input);
        inputsEl.appendChild(labelEl);
      }
    }
  }
  groupEl.appendChild(inputsEl);

  refreshBundleDiscountUI(groupEl);
  return groupEl;
}

type DatasetVariant = {
  id: string;
  availableForSale: boolean;
  title: string;
  imageUrl?: string;
  priceCents?: number;
};

type DatasetProduct = {
  handle: string;
  title: string;
  featuredImageUrl?: string;
  fragrance?: string;
  variants: DatasetVariant[];
};

function isDatasetVariant(val: unknown): val is DatasetVariant {
  if (!isRecord(val)) return false;
  return (
    typeof val.id === "string" &&
    typeof val.availableForSale === "boolean" &&
    typeof val.title === "string"
  );
}

function isDatasetProduct(val: unknown): val is DatasetProduct {
  if (!isRecord(val)) return false;
  if (typeof val.handle !== "string" || typeof val.title !== "string")
    return false;
  if (!hasKey(val, "variants") || !Array.isArray(val.variants)) return false;
  return val.variants.every(isDatasetVariant);
}

function fromDatasetToGroupProducts(
  products: DatasetProduct[]
): GroupProductWithVariants[] {
  return products.map((datasetProduct) => ({
    handle: datasetProduct.handle,
    title: datasetProduct.title,
    featuredImage: datasetProduct.featuredImageUrl
      ? { url: datasetProduct.featuredImageUrl }
      : undefined,
    metafield: {
      value:
        typeof datasetProduct.fragrance === "string"
          ? datasetProduct.fragrance
          : null,
    },
    variants: {
      nodes: datasetProduct.variants.map((datasetVariant) => ({
        id: datasetVariant.id,
        availableForSale: datasetVariant.availableForSale,
        title: datasetVariant.title,
        image: datasetVariant.imageUrl ? { url: datasetVariant.imageUrl } : null,
        price:
          typeof datasetVariant.priceCents === "number"
            ? { amount: (datasetVariant.priceCents / 100).toFixed(2) }
            : null,
        priceV2: null,
      })),
    },
  }));
}

async function initializeBundleGroup(placeholder: HTMLElement) {
  const currentHandle = placeholder.getAttribute("data-current-handle") || "";
  const inputName =
    placeholder.getAttribute("data-input-name") || "bundle_dynamic";

  const json = placeholder.getAttribute("data-products");
  const qtyAttr = placeholder.getAttribute("data-quantity") || "";
  const quantity = (() => {
    const parsed = parseInt(qtyAttr, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  })();
  if (json) {
    try {
      const parsedData: unknown = JSON.parse(json);
      const items = Array.isArray(parsedData) ? parsedData : [];
      const datasetProducts = items.filter(isDatasetProduct);
      if (datasetProducts.length > 0) {
        const groupProducts = fromDatasetToGroupProducts(datasetProducts);
        const groupEl = createBundleGroupElement(
          groupProducts,
          currentHandle,
          inputName,
          quantity
        );
        placeholder.replaceWith(groupEl);
        document.dispatchEvent(new CustomEvent("variant:change"));
        return;
      }
    } catch (err) {
      console.warn(
        "bundle-group-by-tag: Failed to parse data-products JSON; falling back to API fetch.",
        err
      );
    }
  }

  const groupTag = placeholder.getAttribute("data-group-tag") || "";
  if (groupTag) {
    const products = await fetchGroupProductsWithVariants(groupTag);
    if (products && products.length > 0) {
      const groupEl = createBundleGroupElement(
        products,
        currentHandle,
        inputName,
        quantity
      );
      placeholder.replaceWith(groupEl);
      document.dispatchEvent(new CustomEvent("variant:change"));
    }
  }
}

function mountBundleGroups(root: ParentNode = document) {
  const selectors = "[data-bundle-group-products], [data-bundle-group-by-tag]";
  const placeholders = root.querySelectorAll<HTMLElement>(selectors);
  placeholders.forEach((el) => void initializeBundleGroup(el));
}

const init = () => mountBundleGroups();
document.readyState === "loading"
  ? document.addEventListener("DOMContentLoaded", init)
  : init();
document.addEventListener("shopify:section:load", () => mountBundleGroups());
document.addEventListener("cart:change", () => refreshBundleDiscountUI());
document.addEventListener("cart:refresh", () => refreshBundleDiscountUI());
document.addEventListener("variant:change", () => refreshBundleDiscountUI());
document.addEventListener("bundle:refresh-discount", () =>
  refreshBundleDiscountUI()
);
